// src/pages/bloomterminal/Billing.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard } from 'lucide-react';
import Skeleton from 'react-loading-skeleton';
import {
  useSubscriptionDetails,
  usePaymentMethod,
  usePaymentMethodPortal,
  useReceipts,
  useDownloadInvoice,
} from '../utils/api-hooks/subscription';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { useTheme } from '../context/ThemeContext';
import { useBiqPageContext } from '../context/BiqPageContext';
import { ROUTES } from '../constants/Routes';

import 'react-loading-skeleton/dist/skeleton.css';

const OUTLINE_BUTTON_CLASS =
  "font-['Inter'] font-medium border border-bloom-green text-bloom-green rounded-[8px] px-[16px] py-[8px] hover:bg-bloom-green/10 transition-colors whitespace-nowrap";

const SECTION_DIVIDER_CLASS = 'mt-[24px] mb-[32px] h-[1px] bg-[#E2E2E2] dark:bg-[#282a29]';

const COLUMN_CLASS = 'w-[120px] xl:w-[162px] flex-shrink-0 flex items-center';

const ROW_PADDING_CLASS = 'py-[10px]';

// Module-level so the refresh-on-return survives leaving and re-entering Billing in the same tab.
let portalOpened = false;

const Billing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fontSize } = useGlobalFontSize();
  const { isDark } = useTheme();
  const { setPageExtras } = useBiqPageContext() || {};

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const skeletonColors = {
    baseColor: isDark ? '#282a29' : '#E8EBE9',
    highlightColor: isDark ? '#3a3c3b' : '#f0f5f2',
  };

  // Fetch data from APIs
  const {
    data: subscriptionData,
    isLoading: isLoadingSubscription,
    error: subscriptionError,
  } = useSubscriptionDetails();

  useEffect(() => {
    if (!setPageExtras) return undefined;
    if (isLoadingSubscription && !subscriptionData) {
      setPageExtras(null);
      return () => setPageExtras(null);
    }
    const expiresRaw =
      subscriptionData?.expires_at ?? subscriptionData?.expires_date ?? null;
    let expiresAt = null;
    if (typeof expiresRaw === 'string' && expiresRaw.trim()) {
      expiresAt = expiresRaw.trim();
    } else if (typeof expiresRaw === 'number' && Number.isFinite(expiresRaw)) {
      expiresAt = new Date(expiresRaw).toISOString();
    }
    const tier = subscriptionData?.tier;
    setPageExtras({
      ...(tier ? { tier } : {}),
      ...(typeof subscriptionData?.is_promotional === 'boolean'
        ? { is_promotional: subscriptionData.is_promotional }
        : {}),
      ...(expiresAt ? { expires_at: expiresAt } : {}),
    });
    return () => setPageExtras(null);
  }, [setPageExtras, subscriptionData, isLoadingSubscription]);

  const {
    data: paymentMethodData,
    isLoading: isLoadingPayment,
    error: paymentError,
  } = usePaymentMethod();

  const {
    data: receiptsData,
    isLoading: isLoadingReceipts,
    error: receiptsError,
  } = useReceipts(currentPage, pageSize);
  const downloadInvoice = useDownloadInvoice();
  const paymentPortal = usePaymentMethodPortal();

  const queryClient = useQueryClient();
  const clickLockRef = useRef(false);

  const handleUpdatePayment = () => {
    if (clickLockRef.current) return; // sync guard: state-based disabled lags a double click
    clickLockRef.current = true;
    // Open the tab synchronously (before any await) so popup blockers allow it; the
    // RevenueCat portal has no link back, so Billing stays open in this tab.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    paymentPortal.mutate({ tab }, {
      onSuccess: () => { portalOpened = true; },
      onSettled: () => { clickLockRef.current = false; },
    });
  };

  // Back from the portal: refetch the card and plan (the 5 min staleTime would hide changes).
  useEffect(() => {
    const refresh = () => {
      if (!portalOpened || document.visibilityState !== 'visible') return;
      queryClient.invalidateQueries({ queryKey: ['paymentMethod'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [queryClient]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format price for display (amount is in decimal, e.g., 29.99)
  const formatPrice = (amount, currency = 'USD') => {
    if (amount == null) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatPlanName = (tier) => {
    if (!tier || tier === 'free') return t('billing.freePlan');
    const tierName = tier === 'ifpa' ? 'IFPA' : tier.charAt(0).toUpperCase() + tier.slice(1);
    return t('usage.planName', { plan: tierName });
  };

  // Format payment method display name
  const formatPaymentMethodName = (method) => {
    const methodNames = {
      paypal: 'PayPal',
      apple_pay: 'Apple Pay',
      google_wallet: 'Google Wallet',
      credit_card: 'Credit Card',
      rc_billing: t('billing.cardOnFile'),
      stripe: t('billing.cardOnFile'),
      app_store: 'App Store',
      play_store: 'Google Play',
    };
    return methodNames[method] || method;
  };

  const renewalSource =
    subscriptionData?.next_billing_date || subscriptionData?.expires_date;

  const currentPlan = {
    name: formatPlanName(subscriptionData?.tier),
    renewalDate: formatDate(renewalSource),
    expiresDate: formatDate(subscriptionData?.expires_date),
    status: subscriptionData?.subscription_status,
    isCanceled: subscriptionData?.has_canceled,
    isPromotional: subscriptionData?.is_promotional,
  };

  // No explicit `will_renew` flag comes back from the API, so an active, paid,
  // non-canceled and non-promotional subscription is one that auto renews.
  const willRenew =
    subscriptionData?.will_renew ??
    (currentPlan.status === 'active' &&
      !currentPlan.isCanceled &&
      !currentPlan.isPromotional &&
      subscriptionData?.tier &&
      subscriptionData.tier !== 'free');

  const paymentMethod = {
    type: formatPaymentMethodName(paymentMethodData?.payment_method_type || paymentMethodData?.payment_method),
    rawType: paymentMethodData?.payment_method_type || paymentMethodData?.payment_method,
    isPromotional: paymentMethodData?.payment_method_type === 'promotional',
    // Card details are only present for card-backed methods.
    brand: paymentMethodData?.card_brand || paymentMethodData?.brand,
    last4: paymentMethodData?.last4 || paymentMethodData?.last_four,
    // Ready-made text from the backend, e.g. 'Apple Pay - Mastercard - 9800' (same as on the receipt).
    label: paymentMethodData?.payment_method_label,
    cardSource: paymentMethodData?.card_source,
  };

  const invoices = (receiptsData?.receipts || []).map((receipt) => ({
    id: receipt.transaction_id,
    invoiceId: receipt.invoice_id,
    isPaid: receipt.status !== 'unpaid',
    date: formatDate(receipt.date),
    dateRaw: receipt.date,
    price: formatPrice(receipt.price, receipt.currency),
    receiptUrl: receipt.receipt_url,
  }));

  const pagination = receiptsData?.pagination || {
    page: 1,
    page_size: pageSize,
    total: 0,
    total_pages: 0,
  };

  const currentTier = subscriptionData?.tier || 'free';
  const isNonPayingTier = currentTier === 'free' || currentTier === 'ifpa';
  const shouldGreyOutInvoices = isNonPayingTier && invoices.length === 0;

  const isDownloading = (invoice, kind) =>
    downloadInvoice.isPending
    && downloadInvoice.variables?.invoiceId === invoice.invoiceId
    && downloadInvoice.variables?.kind === kind;

  const openStoredDocument = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDocument = (invoice, kind) => {
    if (invoice.invoiceId) {
      if (!invoice.isPaid || downloadInvoice.isPending) return;
      // A stored receipt downloads straight away; anything else is built once, then stored.
      if (kind === 'receipt' && invoice.receiptUrl) {
        openStoredDocument(invoice.receiptUrl);
        return;
      }
      downloadInvoice.mutate({ invoiceId: invoice.invoiceId, kind });
    } else if (kind === 'receipt' && invoice.receiptUrl) {
      window.open(invoice.receiptUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderDocumentActions = (invoice, colorClass) => {
    const kinds = invoice.invoiceId ? ['invoice', 'receipt'] : ['receipt'];
    return (
      <div className="flex items-center gap-[16px]">
        {kinds.map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => handleDocument(invoice, kind)}
            disabled={downloadInvoice.isPending}
            className={`underline underline-offset-[3px] transition-colors disabled:cursor-wait disabled:opacity-60 ${colorClass}`}
            style={{ fontSize }}
          >
            {isDownloading(invoice, kind)
              ? t('billing.downloading')
              : t(kind === 'invoice' ? 'billing.invoiceDoc' : 'billing.receiptDoc')}
          </button>
        ))}
      </div>
    );
  };

  const planSubtitle = () => {
    if (subscriptionError) return null;
    if (currentPlan.isCanceled && currentPlan.expiresDate) {
      return t('billing.canceledInfo', { date: currentPlan.expiresDate });
    }
    if (currentPlan.isPromotional && currentPlan.expiresDate) {
      return t('billing.expiresInfo', { date: currentPlan.expiresDate });
    }
    if (willRenew && currentPlan.renewalDate) {
      return t('billing.renewalInfo', { date: currentPlan.renewalDate });
    }
    return null;
  };

  const renderPaymentMethodText = () => {
    const mutedClass = "font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]";
    const strongClass = "font-['Inter'] font-normal text-[14px] leading-[21px] text-[#191919] dark:text-[#f0f5f2]";

    if (paymentError) {
      return <span className={strongClass} style={{ fontSize }}>{t('billing.errorLoading')}</span>;
    }

    if (isNonPayingTier) {
      return (
        <span className={mutedClass} style={{ fontSize }}>
          {currentTier === 'ifpa' ? t('billing.trialTier') : t('billing.freeTier')}
        </span>
      );
    }

    if (paymentMethod.isPromotional) {
      return <span className={strongClass} style={{ fontSize }}>{t('billing.promotionalAccess')}</span>;
    }

    if (paymentMethod.label) {
      // 'current' = the card on file (changed in the portal after the last charge). Anything
      // else is the last successful payment's card, which can lag a portal change.
      if (paymentMethod.rawType === 'rc_billing' && paymentMethod.cardSource !== 'current') {
        return (
          <span className="flex flex-col gap-[2px] min-w-0">
            <span className={strongClass} style={{ fontSize }}>
              {t('billing.lastPayment')} {paymentMethod.label}
            </span>
            <span className={mutedClass} style={{ fontSize: fontSize - 2 }}>
              {t('billing.cardChangeHint')}
            </span>
          </span>
        );
      }
      return <span className={strongClass} style={{ fontSize }}>{paymentMethod.label}</span>;
    }

    if (paymentMethod.brand && paymentMethod.last4) {
      return (
        <span className={`${strongClass} flex items-center gap-[8px]`} style={{ fontSize }}>
          <span>{paymentMethod.brand}</span>
          <span className="text-[#828382] dark:text-[#5d625f] tracking-[0.2em]">••••</span>
          <span>{paymentMethod.last4}</span>
        </span>
      );
    }

    if (paymentMethod.rawType) {
      return <span className={strongClass} style={{ fontSize }}>{paymentMethod.type}</span>;
    }

    return <span className={mutedClass} style={{ fontSize }}>{t('billing.noPaymentMethod')}</span>;
  };

  return (
    <div className="w-full h-full overflow-y-auto overflow-x-hidden bg-dark-app">
      <div className="w-full pt-[24px] pb-[32px] px-[16px] md:px-[32px]">
        {/* Plan header: name + renewal note on the left, Manage subscription on the right */}
        <div className="flex flex-col gap-[16px] sm:flex-row sm:items-start sm:justify-between">
          {isLoadingSubscription ? (
            <>
              <div className="flex flex-col gap-[2px] min-w-0">
                <Skeleton width={200} height={27} {...skeletonColors} />
                <Skeleton width={260} height={21} {...skeletonColors} />
              </div>
              <Skeleton width={180} height={40} {...skeletonColors} />
            </>
          ) : (
            <>
              <div className="flex flex-col gap-[2px] min-w-0">
                <h1 className="font-['Inter'] font-semibold text-[18px] leading-[27px] text-[#191919] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 4 }}>
                  {subscriptionError ? t('billing.errorLoading') : currentPlan.name || t('billing.noPlan')}
                </h1>
                {planSubtitle() && (
                  <p className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382] break-words" style={{ fontSize }}>
                    {planSubtitle()}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => navigate(ROUTES.SUBSCRIPTION)}
                className={`self-start shrink-0 ${OUTLINE_BUTTON_CLASS}`}
                style={{ fontSize }}
              >
                {t('billing.manageSubscription')}
              </button>
            </>
          )}
        </div>

        <div className={SECTION_DIVIDER_CLASS} />

        <div className="flex flex-col gap-[16px] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-[16px] min-w-0">
            <h2 className="font-['Inter'] font-semibold text-[18px] leading-[27px] text-[#191919] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 4 }}>
              {t('billing.payment')}
            </h2>

            {isLoadingPayment ? (
              <div className="flex items-center gap-[12px]">
                <Skeleton width={24} height={24} {...skeletonColors} />
                <Skeleton width={150} height={21} {...skeletonColors} />
              </div>
            ) : (
              <div className="flex items-center gap-[12px] min-w-0">
                <CreditCard
                  size={20}
                  className={`flex-shrink-0 ${isNonPayingTier ? 'text-[#828382] dark:text-[#5d625f]' : 'text-[#5D625F] dark:text-[#f0f5f2]'}`}
                />
                {renderPaymentMethodText()}
              </div>
            )}
          </div>

          {!isLoadingPayment && !paymentMethod.isPromotional && paymentMethod.rawType === 'rc_billing' && (
            <div className="flex flex-col items-start gap-[4px] self-start sm:self-auto sm:items-end shrink-0">
            <button
              type="button"
              onClick={handleUpdatePayment}
              disabled={isNonPayingTier || paymentPortal.isPending}
              className={`font-['Inter'] font-medium rounded-[8px] px-[16px] py-[8px] border transition-colors whitespace-nowrap ${
                isNonPayingTier
                  ? 'border-[#828382] dark:border-[#5d625f] text-[#828382] dark:text-[#5d625f] cursor-not-allowed'
                  : 'border-bloom-green text-bloom-green hover:bg-bloom-green/10'
              }`}
              style={{ fontSize }}
            >
              {paymentPortal.isPending ? t('common.loading') : t('common.update')}
            </button>
            {paymentPortal.isError && (
              <p role="alert" className="font-['Inter'] text-[#d92d20] dark:text-[#f97066]" style={{ fontSize: fontSize - 2 }}>
                {t('billing.updatePaymentError')}
              </p>
            )}
            </div>
          )}
        </div>

        <div className={SECTION_DIVIDER_CLASS} />

        {/* Invoices */}
        <div className="flex flex-col gap-[24px]">
          <h2 className={`font-['Inter'] font-semibold text-[18px] leading-[27px] ${shouldGreyOutInvoices ? 'text-[#828382] dark:text-[#5d625f]' : 'text-[#191919] dark:text-[#f0f5f2]'}`} style={{ fontSize: fontSize + 4 }}>
            {t('billing.invoices')}
          </h2>

          {receiptsData?.sources?.revenuecat === 'unavailable' && invoices.length > 0 && (
            <p className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize }}>
              {t('billing.invoicesUnavailable')}
            </p>
          )}

          {downloadInvoice.isError && (
            <p className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#ef4444] dark:text-[#ff6b6b]" style={{ fontSize }}>
              {t('billing.downloadFailed')}
            </p>
          )}

          {isLoadingReceipts ? (
            <>
              {/* Desktop skeleton */}
              <div className="hidden lg:flex flex-col gap-[20px]">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-[40px]">
                    <Skeleton width={120} height={21} {...skeletonColors} />
                    <Skeleton width={80} height={21} {...skeletonColors} />
                    <Skeleton width={100} height={21} {...skeletonColors} />
                    <Skeleton width={100} height={21} {...skeletonColors} />
                  </div>
                ))}
              </div>
              {/* Mobile skeleton */}
              <div className="lg:hidden flex flex-col gap-[12px]">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white dark:bg-[#1d1d1d] border border-[#E8EBE9] dark:border-[#30363d] rounded-[12px] p-[20px] flex flex-col gap-[16px]">
                    <div className="flex items-center justify-between">
                      <Skeleton width={100} height={44} {...skeletonColors} />
                      <Skeleton width={80} height={44} {...skeletonColors} />
                    </div>
                    <div className="flex items-center justify-between pt-[12px] border-t border-[#E8EBE9] dark:border-[#282a29]">
                      <Skeleton width={80} height={40} {...skeletonColors} />
                      <Skeleton width={60} height={24} {...skeletonColors} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : receiptsError ? (
            <p className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize }}>
              {t('billing.errorLoading')}
            </p>
          ) : invoices.length === 0 ? (
            <p className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize }}>
              {receiptsData?.sources?.revenuecat === 'unavailable' ? t('billing.invoicesUnavailable') : t('billing.noInvoices')}
            </p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto -mx-[16px] md:-mx-[32px] px-[16px] md:px-[32px]">
                <div className="min-w-[560px]">
                  {/* Table header */}
                  <div className={`flex font-['Inter'] font-semibold text-[14px] leading-[21px] text-[#191919] dark:text-[#f0f5f2] mb-[8px] ${ROW_PADDING_CLASS}`}>
                    <div className={`${COLUMN_CLASS} h-[21px]`} style={{ fontSize }}>{t('billing.date')}</div>
                    <div className={`${COLUMN_CLASS} h-[21px]`} style={{ fontSize }}>{t('billing.price')}</div>
                    <div className={`${COLUMN_CLASS} h-[21px]`} style={{ fontSize }}>{t('billing.status')}</div>
                    <div className={`${COLUMN_CLASS} h-[21px]`} style={{ fontSize }}>{t('billing.documents')}</div>
                  </div>

                  {/* Table rows */}
                  <div className="flex flex-col">
                    {invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className={`flex font-['Inter'] font-normal text-[14px] leading-[21px] text-[#191919] dark:text-[#f0f5f2] ${ROW_PADDING_CLASS}`}
                      >
                        <div className={COLUMN_CLASS} style={{ fontSize }}>{invoice.date}</div>
                        <div className={COLUMN_CLASS} style={{ fontSize }}>{invoice.price}</div>
                        <div className={COLUMN_CLASS} style={{ fontSize }}>{invoice.isPaid ? t('billing.paid') : t('billing.unpaid')}</div>
                        <div className={COLUMN_CLASS}>
                          {renderDocumentActions(invoice, 'hover:text-bloom-green')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:hidden flex flex-col gap-[12px]">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="bg-white dark:bg-[#1d1d1d] border border-[#E8EBE9] dark:border-[#30363d] rounded-[12px] p-[20px] flex flex-col gap-[16px]"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-[4px]">
                        <span className="font-['Inter'] font-semibold text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize: fontSize - 2 }}>
                          {t('billing.date')}
                        </span>
                        <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#191919] dark:text-[#f0f5f2]" style={{ fontSize }}>
                          {invoice.date}
                        </span>
                      </div>
                      <div className="flex flex-col gap-[4px] items-end">
                        <span className="font-['Inter'] font-semibold text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize: fontSize - 2 }}>
                          {t('billing.price')}
                        </span>
                        <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#191919] dark:text-[#f0f5f2]" style={{ fontSize }}>
                          {invoice.price}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-[12px] border-t border-[#E8EBE9] dark:border-[#282a29]">
                      <div className="flex flex-col gap-[4px]">
                        <span className="font-['Inter'] font-semibold text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize: fontSize - 2 }}>
                          {t('billing.status')}
                        </span>
                        <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#191919] dark:text-[#f0f5f2]" style={{ fontSize }}>
                          {invoice.isPaid ? t('billing.paid') : t('billing.unpaid')}
                        </span>
                      </div>
                      {renderDocumentActions(invoice, 'text-bloom-green hover:text-bloom-green/80')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-[8px] mt-[24px]">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`px-[12px] py-[6px] rounded-[6px] border font-['Inter'] font-medium text-[14px] transition-colors ${
                      currentPage === 1
                        ? 'border-[#E8EBE9] dark:border-[#282a29] text-[#828382] dark:text-[#5d625f] cursor-not-allowed'
                        : 'border-[#E8EBE9] dark:border-[#282a29] text-[#191919] dark:text-[#f0f5f2] hover:border-bloom-green hover:text-bloom-green'
                    }`}
                    style={{ fontSize: fontSize - 2 }}
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-[4px]">
                    {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      const showPage =
                        page === 1 ||
                        page === pagination.total_pages ||
                        (page >= currentPage - 1 && page <= currentPage + 1);

                      const showEllipsisBefore = page === currentPage - 1 && currentPage > 3;
                      const showEllipsisAfter = page === currentPage + 1 && currentPage < pagination.total_pages - 2;

                      if (!showPage && !showEllipsisBefore && !showEllipsisAfter) {
                        return null;
                      }

                      if (showEllipsisBefore || showEllipsisAfter) {
                        return (
                          <span
                            key={`ellipsis-${page}`}
                            className="px-[8px] text-[#828382] dark:text-[#5d625f] font-['Inter']"
                            style={{ fontSize: fontSize - 2 }}
                          >
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[32px] px-[8px] py-[6px] rounded-[6px] font-['Inter'] font-medium text-[14px] transition-colors ${
                            currentPage === page
                              ? 'bg-bloom-green text-white'
                              : 'text-[#191919] dark:text-[#f0f5f2] hover:bg-bloom-green/10'
                          }`}
                          style={{ fontSize: fontSize - 2 }}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                    disabled={currentPage === pagination.total_pages}
                    className={`px-[12px] py-[6px] rounded-[6px] border font-['Inter'] font-medium text-[14px] transition-colors ${
                      currentPage === pagination.total_pages
                        ? 'border-[#E8EBE9] dark:border-[#282a29] text-[#828382] dark:text-[#5d625f] cursor-not-allowed'
                        : 'border-[#E8EBE9] dark:border-[#282a29] text-[#191919] dark:text-[#f0f5f2] hover:border-bloom-green hover:text-bloom-green'
                    }`}
                    style={{ fontSize: fontSize - 2 }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
